(function () {
  'use strict';

  const PROD_API = 'https://living-crystals.vercel.app/api';
  const LOCAL_API = 'http://localhost:5000/api';
  const STORAGE_KEY = 'lc_pdp_reviews_v1';

  function resolveApiBase() {
    const configured = window.LC_REVIEWS && window.LC_REVIEWS.apiBase;
    const host = window.location.hostname;
    const isLocalDev = host === '127.0.0.1' || host === 'localhost';
    if (isLocalDev) return LOCAL_API;
    if (configured && !String(configured).includes('localhost')) return configured;
    return PROD_API;
  }

  function newReviewId() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'lc-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }

  function computeAggregate(reviews) {
    if (!reviews.length) return { average: 0, count: 0 };
    const sum = reviews.reduce(function (acc, r) {
      return acc + Math.min(5, Math.max(1, Number(r.rating) || 0));
    }, 0);
    return {
      average: Math.round((sum / reviews.length) * 10) / 10,
      count: reviews.length,
    };
  }

  function readLocalStore() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  function writeLocalStore(store) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
      /* quota / private mode */
    }
  }

  function getLocalReviews(handle) {
    if (!handle) return [];
    const store = readLocalStore();
    return Array.isArray(store[handle]) ? store[handle] : [];
  }

  function saveLocalReview(handle, entry) {
    const store = readLocalStore();
    const list = Array.isArray(store[handle]) ? store[handle].slice() : [];
    list.push(entry);
    store[handle] = list.slice(-200);
    writeLocalStore(store);
    return list;
  }

  function mergeReviewLists(primary, secondary) {
    const map = new Map();
    primary.concat(secondary).forEach(function (r) {
      if (!r || !r.id) return;
      map.set(r.id, r);
    });
    return Array.from(map.values());
  }

  function parseInitial(root) {
    const raw = root.getAttribute('data-initial-reviews');
    if (!raw) return { reviews: [], aggregate: { average: 0, count: 0 } };
    try {
      const data = JSON.parse(raw);
      return {
        reviews: Array.isArray(data.reviews) ? data.reviews : [],
        aggregate: data.aggregate || { average: 0, count: 0 },
      };
    } catch {
      return { reviews: [], aggregate: { average: 0, count: 0 } };
    }
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  }

  function updateSummary(root, aggregate) {
    const avg = aggregate && aggregate.count ? aggregate.average : 0;
    const count = aggregate && aggregate.count ? aggregate.count : 0;
    const stars = root.querySelector('[data-lc-reviews-stars]');
    const score = root.querySelector('[data-lc-reviews-score]');
    const countEl = root.querySelector('[data-lc-reviews-count]');
    if (stars) stars.style.setProperty('--lc-rating', String(avg));
    if (score) {
      score.innerHTML = count
        ? `${avg}<span class="lc-pdp__reviews-out-of"> / 5</span>`
        : '—';
    }
    if (countEl) {
      countEl.textContent = count
        ? `${count} customer review${count === 1 ? '' : 's'}`
        : 'No reviews yet';
    }
    const summary = root.querySelector('[data-lc-reviews-summary]');
    if (summary) {
      summary.setAttribute(
        'aria-label',
        count ? `${avg} out of 5 stars, ${count} customer reviews` : 'No customer reviews yet'
      );
    }
  }

  function renderList(listEl, reviews) {
    if (!listEl) return;
    if (!reviews.length) {
      listEl.innerHTML = '<p class="lc-pdp__reviews-list-empty">No written reviews yet. Yours could be the first.</p>';
      return;
    }
    const sorted = reviews.slice().sort(function (a, b) {
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
    listEl.innerHTML = sorted
      .map(function (r) {
        const rating = Math.min(5, Math.max(1, Number(r.rating) || 5));
        return (
          '<article class="lc-pdp__review-item">' +
          '<header class="lc-pdp__review-item-head">' +
          '<span class="lc-pdp__review-item-stars" style="--lc-rating:' +
          rating +
          ';" aria-hidden="true"></span>' +
          '<span class="lc-pdp__review-item-meta">' +
          escapeHtml(r.name || 'Customer') +
          (r.createdAt ? ' · ' + escapeHtml(formatDate(r.createdAt)) : '') +
          '</span>' +
          '</header>' +
          '<p class="lc-pdp__review-item-body">' +
          escapeHtml(r.body || '') +
          '</p>' +
          '</article>'
        );
      })
      .join('');
  }

  function setStarInput(form, value) {
    const hidden = form.querySelector('[name="rating"]');
    if (hidden) hidden.value = String(value);
    form.querySelectorAll('[data-lc-star]').forEach(function (btn) {
      const star = Number(btn.getAttribute('data-lc-star'));
      btn.classList.toggle('is-active', star <= value);
      btn.setAttribute('aria-pressed', star <= value ? 'true' : 'false');
    });
  }

  function initInteractiveStars(form) {
    let current = Number(form.querySelector('[name="rating"]')?.value) || 0;
    form.querySelectorAll('[data-lc-star]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        current = Number(btn.getAttribute('data-lc-star'));
        setStarInput(form, current);
      });
      btn.addEventListener('mouseenter', function () {
        const hover = Number(btn.getAttribute('data-lc-star'));
        form.querySelectorAll('[data-lc-star]').forEach(function (b) {
          const star = Number(b.getAttribute('data-lc-star'));
          b.classList.toggle('is-active', star <= hover);
        });
      });
    });
    form.addEventListener('mouseleave', function () {
      setStarInput(form, current);
    });
  }

  async function fetchReviewsFromApi(apiBase, handle) {
    const url = `${apiBase}/reviews?handle=${encodeURIComponent(handle)}`;
    const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json().catch(function () {
      return null;
    });
    if (!data || !data.ok) return null;
    return data;
  }

  async function postReviewToApi(apiBase, payload) {
    const res = await fetch(`${apiBase}/reviews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(function () {
      return null;
    });
    if (!res.ok || !data || !data.ok) {
      const msg = (data && data.error) || 'api_unavailable';
      const err = new Error(msg);
      err.apiFailed = true;
      throw err;
    }
    return data;
  }

  async function notifyMerchantViaContact(root, payload) {
    const productTitle = root.getAttribute('data-product-title') || 'Product';
    const productUrl = window.location.href.split('#')[0];
    const email = String(payload.email || '').trim();
    const contactEmail = email || 'reviews@customers.local';

    const message =
      'New product review submitted on the storefront.\n\n' +
      'Product: ' +
      productTitle +
      '\nLink: ' +
      productUrl +
      '\nRating: ' +
      payload.rating +
      '/5\n\n' +
      String(payload.body || '');

    const params = new URLSearchParams();
    params.append('form_type', 'contact');
    params.append('utf8', '✓');
    params.append('contact[name]', String(payload.name || 'Customer').slice(0, 80));
    params.append('contact[email]', contactEmail.slice(0, 120));
    params.append('contact[body]', message.slice(0, 5000));

    try {
      await fetch('/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'text/html,application/xhtml+xml',
        },
        body: params.toString(),
        credentials: 'same-origin',
        redirect: 'manual',
      });
    } catch {
      /* optional notification */
    }
  }

  function applyState(root, listEl, reviews) {
    const aggregate = computeAggregate(reviews);
    updateSummary(root, aggregate);
    renderList(listEl, reviews);
    return { reviews: reviews, aggregate: aggregate };
  }

  function initRoot(root) {
    if (!root || root.dataset.lcReviewsInit === 'true') return;
    root.dataset.lcReviewsInit = 'true';

    const handle = root.getAttribute('data-product-handle');
    const productId = root.getAttribute('data-product-id');
    const apiBase = resolveApiBase();
    const form = root.querySelector('[data-lc-reviews-form]');
    const listEl = root.querySelector('[data-lc-reviews-list]');
    const statusEl = root.querySelector('[data-lc-reviews-status]');
    const customForm = root.getAttribute('data-use-custom-form') === 'true';

    if (!customForm) return;

    const initial = parseInitial(root);
    const local = getLocalReviews(handle);
    let state = applyState(root, listEl, mergeReviewLists(initial.reviews, local));

    if (form) {
      initInteractiveStars(form);
      form.addEventListener('submit', async function (ev) {
        ev.preventDefault();
        if (statusEl) statusEl.textContent = '';

        const fd = new FormData(form);
        const rating = Number(fd.get('rating'));
        const name = String(fd.get('name') || '').trim();
        const body = String(fd.get('body') || '').trim();
        const email = String(fd.get('email') || '').trim();
        const honeypot = String(fd.get('website') || '').trim();

        if (honeypot) return;

        if (!rating || rating < 1) {
          if (statusEl) statusEl.textContent = 'Please select a star rating.';
          return;
        }
        if (!name) {
          if (statusEl) statusEl.textContent = 'Please enter your name.';
          return;
        }
        if (body.length < 5) {
          if (statusEl) statusEl.textContent = 'Please write a little more in your review (at least 5 characters).';
          return;
        }

        const payload = {
          handle: handle,
          product_id: productId,
          rating: rating,
          name: name,
          email: email,
          body: body,
          website: honeypot,
        };

        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;
        if (statusEl) statusEl.textContent = 'Submitting…';

        try {
          let savedViaApi = false;
          try {
            const data = await postReviewToApi(apiBase, payload);
            state = applyState(root, listEl, data.reviews || []);
            savedViaApi = true;
          } catch (apiErr) {
            const entry = {
              id: newReviewId(),
              rating: rating,
              name: name,
              body: body,
              email: email,
              createdAt: new Date().toISOString(),
              status: 'published',
              source: 'local',
            };
            const merged = mergeReviewLists(state.reviews, [entry]);
            saveLocalReview(handle, entry);
            state = applyState(root, listEl, merged);
            notifyMerchantViaContact(root, payload);
          }

          form.reset();
          setStarInput(form, 0);
          if (statusEl) {
            statusEl.textContent = savedViaApi
              ? 'Thank you! Your review has been published.'
              : 'Thank you! Your review has been published on this product page.';
          }
        } catch (err) {
          if (statusEl) statusEl.textContent = err.message || 'Something went wrong. Please try again.';
        } finally {
          if (submitBtn) submitBtn.disabled = false;
        }
      });
    }

    if (handle) {
      fetchReviewsFromApi(apiBase, handle)
        .then(function (data) {
          if (!data) return;
          const merged = mergeReviewLists(data.reviews || [], getLocalReviews(handle));
          state = applyState(root, listEl, merged);
        })
        .catch(function () {
          /* keep merged initial + local */
        });
    }
  }

  function init() {
    document.querySelectorAll('[data-lc-pdp-reviews]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
